import { useMemo, useRef, useState } from "react";
import { m } from "@/paraglide/messages";

const COLORS = [
  "#3b82f6",
  "#ef4444",
  "#22c55e",
  "#f59e0b",
  "#a855f7",
  "#ec4899",
  "#14b8a6",
  "#f97316",
];

const R = 140;
const CX = 150;
const CY = 150;

function segmentPath(startDeg: number, endDeg: number): string {
  // Angles measured clockwise from the top (12 o'clock).
  const toXY = (deg: number) => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return [CX + R * Math.cos(rad), CY + R * Math.sin(rad)];
  };
  const [x1, y1] = toXY(startDeg);
  const [x2, y2] = toXY(endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${CX} ${CY} L ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} Z`;
}

export function DecisionWheel() {
  const [raw, setRaw] = useState("Pizza\nSushi\nTacos\nBurgers\nSalad\nCurry");
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [winner, setWinner] = useState<string | null>(null);
  const rotationRef = useRef(0);

  const options = useMemo(
    () =>
      raw
        .split("\n")
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
    [raw],
  );

  const seg = options.length > 0 ? 360 / options.length : 360;

  const spin = () => {
    if (spinning || options.length < 2) return;
    const i = Math.floor(Math.random() * options.length);
    const center = (i + 0.5) * seg;
    const next =
      rotationRef.current +
      360 * 5 +
      ((360 - (center % 360)) % 360) +
      (360 - (rotationRef.current % 360));
    rotationRef.current = next;
    setRotation(next);
    setWinner(null);
    setSpinning(true);
    // Resolve the winner after the CSS transition ends (4s).
    window.setTimeout(() => {
      setWinner(options[i]);
      setSpinning(false);
    }, 4200);
  };

  return (
    <div className="sci-card rounded-2xl space-y-5 p-6 sm:p-8">
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-1.5">
          <label
            htmlFor="wheel-options"
            className="block text-sm text-muted-foreground"
          >
            {m.dw_options()}
          </label>
          <textarea
            id="wheel-options"
            rows={7}
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={m.dw_placeholder()}
            className="w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm text-neutral-900"
          />
        </div>

        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div
              className="absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1 text-2xl"
              aria-hidden="true"
            >
              ▼
            </div>
            <svg
              viewBox="0 0 300 300"
              className="h-64 w-64"
              role="img"
              aria-label={m.dw_wheel_alt()}
            >
              <title>{m.dw_wheel_alt()}</title>
              <g
                data-testid="dw-wheel"
                style={{
                  transform: `rotate(${rotation}deg)`,
                  transformOrigin: "150px 150px",
                  transition: spinning
                    ? "transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)"
                    : "none",
                }}
              >
                {options.map((opt, i) => {
                  const start = i * seg;
                  const mid = start + seg / 2;
                  const rad = ((mid - 90) * Math.PI) / 180;
                  const tx = CX + R * 0.62 * Math.cos(rad);
                  const ty = CY + R * 0.62 * Math.sin(rad);
                  return (
                    <g key={`${opt}-${i}`}>
                      <path
                        d={segmentPath(start, start + seg)}
                        fill={COLORS[i % COLORS.length]}
                      />
                      <text
                        x={tx}
                        y={ty}
                        fill="#fff"
                        fontSize="12"
                        fontWeight="600"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        transform={`rotate(${mid} ${tx} ${ty})`}
                      >
                        {opt.length > 10 ? `${opt.slice(0, 9)}…` : opt}
                      </text>
                    </g>
                  );
                })}
              </g>
            </svg>
          </div>

          <button
            type="button"
            onClick={spin}
            disabled={spinning || options.length < 2}
            className="sci-result-zone sci-result rounded-md px-6 py-2.5 text-sm font-semibold disabled:opacity-50"
          >
            {spinning ? m.dw_spinning() : m.dw_spin()}
          </button>

          <p
            data-testid="dw-winner"
            className="min-h-6 text-center font-display text-xl font-bold"
          >
            {winner ? `🎉 ${winner}` : ""}
          </p>
        </div>
      </div>
    </div>
  );
}
